import React, { useState, useEffect } from 'react';
import {
  Button,
  Card,
  Divider,
  Space,
  Table,
  Tag,
  Typography,
  Select,
} from 'antd';
import {
  CloudServerOutlined,
  ReloadOutlined,
  PlayCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { App as AntApp } from 'antd';
import { admin } from '../services/api';
import styles from './SSW.module.css';

const { Title, Text } = Typography;

interface SswStatus {
  estado: string;
  navegadorId?: string;
  url?: string;
}

const SSWComponent: React.FC = () => {
  const { message } = AntApp.useApp();
  const [loading, setLoading] = useState(false);
  const [isBrowserLoading, setIsBrowserLoading] = useState(false);
  const [sswStatus, setSswStatus] = useState<SswStatus | null>(null);

  // Acessos ao ERP: cada usuário com credencial (erpUsuario) vinculado à filial.
  const [sswUsers, setSswUsers] = useState<any[]>([]);
  const [sswUsersLoading, setSswUsersLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const loadAccess = async () => {
    try {
      setSswUsersLoading(true);
      // Funcionários digitais (isDigitalWorker) da organização: retornados
      // diretamente pelo backend, são os usuários usados pelo Playwright para
      // login por romaneio e demais automações do ERP.
      const users = await admin.getDigitalWorkers();
      const mapped = (users || []).map((u) => ({
        id: u.id,
        name: u.name,
        codigo: u.codigo,
        erpDominio: u.erpDominio,
        erpCpf: u.erpCpf,
        erpUsuario: u.erpUsuario,
        branch: u.assignedBranch?.name || 'Não atribuída',
      }));
      setSswUsers(mapped);
      setSelectedUserId((prev) =>
        mapped.some((u) => u.id === prev) ? prev : mapped[0]?.id || null
      );
    } catch {
      setSswUsers([]);
    } finally {
      setSswUsersLoading(false);
    }
  };

  useEffect(() => {
    loadAccess();
  }, []);

  const handleIniciarSsw = async () => {
    try {
      setIsBrowserLoading(true);
      const status = await admin.iniciarSsw({ userId: selectedUserId });
      setSswStatus(status);
      message.success(`Navegador do ERP iniciado: ${status.estado}`);
    } catch (error) {
      message.error((error as Error).message || 'Erro ao iniciar navegador do ERP');
    } finally {
      setIsBrowserLoading(false);
    }
  };

  const handleCheckStatus = async () => {
    try {
      const status = await admin.sswStatus();
      setSswStatus(status);
    } catch (error) {
      message.error((error as Error).message || 'Erro ao verificar status');
    }
  };

  const handleFecharSsw = async () => {
    try {
      await admin.fecharSsw();
      setSswStatus(null);
      message.success('Sessão do ERP encerrada');
    } catch (error) {
      message.error((error as Error).message || 'Erro ao encerrar sessão');
    }
  };

  const getStatusColor = (estado: string) => {
    switch (estado.toLowerCase()) {
      case 'ativo':
      case 'logado':
        return 'success';
      case 'inativo':
        return 'error';
      case 'iniciando':
        return 'processing';
      case 'aguardando':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <div className={styles.sswContainer}>
      <Card
        title={
          <Space>
            <CloudServerOutlined style={{ color: '#1890ff' }} />
            <Title level={4} style={{ margin: 0 }}>
              Integração com o ERP
            </Title>
          </Space>
        }
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={loadAccess}
            loading={sswUsersLoading}
            type="primary"
            ghost
          >
            Atualizar
          </Button>
        }
        className={`${styles.sswCard} ${styles.sswDashboardCard}`}
        style={{ marginBottom: '24px' }}
      >
        <Text type="secondary">
          Acessos SSW (por filial): funcionários digitais da organização
          (isDigitalWorker). São os usuários usados pelo Playwright para login por
          romaneio e demais automações do ERP.
        </Text>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px', marginBottom: '24px' }}>
        {/* Acessos por usuário */}
        <Card
          title={
            <Space>
              <CloudServerOutlined />
              <Text>Acessos ao ERP</Text>
            </Space>
          }
          extra={<Tag color="blue">Por usuário + filial</Tag>}
          style={{ height: '100%' }}
        >
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Text type="secondary">Acesso usado no navegador:</Text>
            <Select
              style={{ width: '100%' }}
              placeholder="Selecione um acesso"
              value={selectedUserId || undefined}
              onChange={setSelectedUserId}
              loading={sswUsersLoading}
              options={sswUsers.map((u) => ({
                value: u.id,
                label: `${u.name} (${u.branch}) — ${u.erpUsuario}`,
              }))}
              allowClear
            />
            <Table
              size="small"
              rowKey="id"
              loading={sswUsersLoading}
              dataSource={sswUsers}
              pagination={false}
              locale={{ emptyText: 'Nenhum funcionário digital (isDigitalWorker) cadastrado.' }}
              scroll={{ x: 'max-content' }}
              columns={[
                { title: 'Nome', dataIndex: 'name', key: 'name' },
                { title: 'Código', dataIndex: 'codigo', key: 'codigo', width: 130 },
                { title: 'Domínio', dataIndex: 'erpDominio', key: 'erpDominio', width: 90, render: (v: string) => (v ? <Tag>{v}</Tag> : '-') },
                { title: 'CPF', dataIndex: 'erpCpf', key: 'erpCpf', width: 140, render: (v: string) => (v ? v : '-') },
                { title: 'Usuário', dataIndex: 'erpUsuario', key: 'erpUsuario', width: 170 },
                { title: 'Filial', dataIndex: 'branch', key: 'branch', width: 140 },
              ]}
            />
          </Space>
        </Card>

        {/* Status do navegador */}
        <Card
          title={
            <Space>
              <PlayCircleOutlined />
              <Text>Status do Navegador</Text>
            </Space>
          }
          extra={<Tag color="green">Monitoramento</Tag>}
          style={{ height: '100%' }}
        >
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text type="secondary">Estado:</Text>
              {sswStatus ? (
                <Tag color={getStatusColor(sswStatus.estado)}>{sswStatus.estado}</Tag>
              ) : (
                <Tag color="default">Inativo</Tag>
              )}
            </div>

            {(sswStatus?.navegadorId || sswStatus?.url) && (
              <>
                <Divider style={{ margin: '8px 0' }} />
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  {sswStatus?.navegadorId && (
                    <Text type="secondary">ID: {sswStatus.navegadorId}</Text>
                  )}
                  {sswStatus?.url && (
                    <Text type="secondary">URL: {sswStatus.url}</Text>
                  )}
                </Space>
              </>
            )}

            <Divider style={{ margin: '16px 0' }} />
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                onClick={handleIniciarSsw}
                loading={isBrowserLoading}
                block
                disabled={!sswUsers.length}
              >
                Iniciar Navegador
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={handleCheckStatus}
                loading={loading}
                block
              >
                Verificar Status
              </Button>
              <Button
                danger
                icon={<CloseCircleOutlined />}
                onClick={handleFecharSsw}
                loading={isBrowserLoading}
                block
              >
                Encerrar Sessão
              </Button>
            </Space>
          </Space>
        </Card>
      </div>
    </div>
  );
};

export default SSWComponent;